import { createIntegerRollupCompiler } from './integer-layer-rollup';
import { statusProjectionDependencies } from './status-attack-budget';
import { readInventoryField, readInventoryRows } from './inventory-raw-readers';
import { storedStatusRows } from './creature-status-reader';
import { readItemField } from './item-field-reader';
import { rowLayerNames } from './row-layer-names';
import { layerReadView } from './layer-read-view';
import { integerCompileBorrowedRow } from './integer-compute-slots';

type Row = Record<string, unknown>;
const record = (value: unknown): Row => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : {};
const upper = (value: unknown): string => String(value ?? '').toUpperCase();

/** The equip decision has installed its row flags. Rewalk only families named
 * by those items, including their held status/enchantment-derived integers. */
export function applyEquipmentIntegerDelta(row: Row, changedIds: readonly string[], definition: (id: string) => Row | null) {
    const changed = new Set(changedIds.map(upper)), families = new Set<string>();
    const deps = statusProjectionDependencies(row, definition);
    const inventory = readInventoryRows(row, 'inventory').map(entry => {
        const full = deps.resolveInventoryInstance(upper(readInventoryField(entry, 'inventory_id'))) ?? entry;
        if (changed.has(upper(readInventoryField(entry, 'inventory_id')))) {
            for (const key of rowLayerNames(full)) families.add(key);
            for (const key of Object.keys(record(record(record(entry.computed).creature).modifiers))) families.add(key);
            for (const status of storedStatusRows(entry, 'target')) {
                const payload = definition(upper(readItemField(status, 'status_item_id')));
                if (payload) for (const key of rowLayerNames(payload)) families.add(key);
            }
        }
        return full;
    });
    const compiler = createIntegerRollupCompiler(deps), layers = [...families];
    const sources = inventory.map(full => {
        // Inventory light is itself an integer source. A cold held instance has
        // only its saved statuses, so derive that family before scoring the gear.
        if (readItemField(full, 'item_type', deps) !== 'light_source' || !storedStatusRows(full, 'target').length) return full;
        const projected = integerCompileBorrowedRow(full);
        compiler.delta(projected, layers);
        return projected;
    });
    const host = layerReadView(row, { inventory: sources });
    return compiler.delta(host, layers);
}
